import type { Layout } from '@antv/layout/lib/layout/layout'
import { filter, first, get } from 'lodash-es'
import { animationFrameScheduler, Observable, throttleTime } from 'rxjs'
import type { Graph } from '../entities'
import { DEFAULT_LAYOUT_OPTIONS, Layouts, LAYOUT_MESSAGE } from '../layouts'
import type {
  AbstractLayout,
  DraftNormalGraphLayoutData,
  GraphOptions,
  LayoutAlgorithm,
  LayoutConfig,
  LayoutData,
  NormalGraphOptions,
} from '../types'
import { gpuDetector } from '../utils'

type NormalLayoutConfig = NormalGraphOptions['layout'] & {
  width: number
  height: number
  center: [number, number]
  nodeSize: number | number[] | ((d?: unknown) => number) | undefined
  gpuEnabled: boolean
  workerEnabled?: boolean
  // TODO: 支持前置布局
  presetLayout?: unknown
  tick?(): void
}

const mockRaf = (cb: TimerHandler) => setTimeout(cb, 16)
const mockCaf = (reqId: number) => clearTimeout(reqId)

const helper = {
  requestAnimationFrame(callback: FrameRequestCallback) {
    const fn =
      typeof window !== 'undefined'
        ? window.requestAnimationFrame ||
          window.webkitRequestAnimationFrame ||
          mockRaf
        : mockRaf
    return fn(callback)
  },
  cancelAnimationFrame(requestId: number) {
    const fn =
      typeof window !== 'undefined'
        ? window.cancelAnimationFrame ||
          window.webkitCancelAnimationFrame ||
          mockCaf
        : mockCaf
    return fn(requestId)
  },
}

const FORCE_LAYOUT = [
  'force',
  'gForce',
  'gForce-gpu',
  'forceAtlas2',
  'fruchterman',
]
const GPU_LAYOUT = ['fruchterman', 'gForce']

interface AbstractLayoutController {
  execute: LayoutAlgorithm
}

export class LayoutController implements AbstractLayoutController {
  presetLayout?: AbstractLayout
  graph: Graph

  private destroyed = false
  private isGPU?: boolean
  private layoutMethod?: Layout & {
    enableTick?: boolean
    tick?: () => void
    isCustomLayout?: boolean
  }

  private worker?: Worker
  private workerData: {
    requestId?: number
    requestId2?: number
    currentTick?: unknown
    currentTickData?: DraftNormalGraphLayoutData
  }

  constructor(graph: Graph, layoutConfig?: GraphOptions['layout']) {
    this.graph = graph
    this.workerData = {}
  }

  /**
   * 是否有 GPU 版本
   *
   * @param layoutType
   * @returns
   */
  static hasGPUVersion(layoutType: string): boolean {
    return GPU_LAYOUT.includes(layoutType)
  }

  /**
   * 是否需要逐步迭代
   *
   * @param layoutType
   * @returns
   */
  static needTick(layoutType: string): boolean {
    return FORCE_LAYOUT.includes(layoutType)
  }

  /**
   * 获取默认布局配置
   *
   * @returns 默认布局配置
   */
  getDefaultLayoutConfig = (layoutConfig: LayoutConfig) => {
    const defaultConfig = get(DEFAULT_LAYOUT_OPTIONS, layoutConfig.type, {})

    if (defaultConfig.modify) {
      return {
        ...defaultConfig,
        ...defaultConfig.modify(defaultConfig),
      }
    }

    return defaultConfig
  }

  /**
   * 执行一般图布局
   */
  execute = (data: DraftNormalGraphLayoutData, layoutConfig: LayoutConfig) => {
    const { width, height } = this.graph
    const { nodes, edges } = data

    // 节点数组为空直接跳过布局
    if (nodes.length === 0) {
      return new Observable<LayoutData>((subscriber) => {
        subscriber.complete()
      })
    }

    const finalLayoutConfig: NormalLayoutConfig = {
      width,
      height,
      nodeSize: first(nodes)!.size,
      gpuEnabled: false,
      ...this.getDefaultLayoutConfig(layoutConfig),
      ...layoutConfig,
    }

    // 初始化节点到中心
    this.initPositions(finalLayoutConfig.center, nodes, width, height)

    // 防止用户直接用 -gpu 结尾指定布局
    let layoutType = finalLayoutConfig.type

    if (layoutType && layoutType.split('-')[1] === 'gpu') {
      layoutType = layoutType.split('-')[0] as NormalLayoutConfig['type']

      finalLayoutConfig.type = layoutType
      finalLayoutConfig.gpuEnabled = true
    }

    // 若用户指定开启 gpu，且当前浏览器支持 webgl，且该算法存在 GPU 版本（目前仅支持 fruchterman 和 gForce），使用 gpu 版本的布局
    let enableGPU = false

    if (finalLayoutConfig.gpuEnabled) {
      enableGPU = true

      // 打开下面语句将会导致 webWorker 报找不到 window
      if (!gpuDetector().webgl) {
        console.warn(
          `Your browser does not support webGL or GPU. The layout will run in CPU.`
        )

        enableGPU = false
      }
    }

    // 启用 GPU 布局
    this.isGPU = enableGPU
    // 停止原有 worker
    // this.stopWorker()

    // if (
    //   finalLayoutConfig.workerEnabled &&
    //   this.layoutWithWorker(finalLayoutConfig, {
    //     nodes,
    //     edges,
    //   })
    // ) {
    //   // 如果启用布局web worker并且浏览器支持web worker，用web worker布局。否则回退到不用web worker布局。
    //   return true
    // }

    return this.executeLayoutMethod(finalLayoutConfig, {
      nodes,
      edges,
    })
  }

  /**
   * 执行前置布局
   */
  // executePreset() {
  //   if (this.presetLayout) {
  //     this.graph.emit(EVENT_NAME.beforelayout)
  //     this.presetLayout.execute()
  //     this.graph.emit(EVENT_NAME.afterlayout)
  //   }
  // }

  /**
   * 通过 worker 计算布局
   *
   * @param layoutConfig
   * @param data
   */
  private layoutWithWorker = (
    layoutConfig: NormalLayoutConfig,
    data: DraftNormalGraphLayoutData
  ): boolean => {
    const worker = this.getWorker()
    // 每次worker message event handler调用之间的共享数据，会被修改。
    const { workerData } = this

    if (!worker) {
      return false
    }

    workerData.requestId = undefined
    workerData.requestId2 = undefined
    workerData.currentTick = undefined
    workerData.currentTickData = undefined

    let start = Promise.resolve()

    if (layoutConfig.type) {
      start = start.then(() => this.runWebWorker(worker, data, layoutConfig))
    }

    // 最后统一在外部调用onAllLayoutEnd
    start
      .then(() => {
        // if (layoutConfig.onAllLayoutEnd) {
        //   layoutConfig.onAllLayoutEnd()
        // }
      })
      .catch((error) => {
        console.error('layout failed', error)
      })

    return true
  }

  /**
   * 执行布局方法
   *
   * @param layoutConfig
   * @param data
   */
  private executeLayoutMethod = (
    layoutConfig: NormalLayoutConfig,
    data: DraftNormalGraphLayoutData
  ): Observable<LayoutData> =>
    new Observable<LayoutData>((subscriber) => {
      const { graph } = this

      if (!graph || graph.destroyed) {
        return
      }

      let layoutType = layoutConfig.type

      // 若用户指定开启 gpu，且当前浏览器支持 webgl，且该算法存在 GPU 版本（目前仅支持 fruchterman 和 gForce），使用 gpu 版本的布局
      if (layoutType && this.isGPU) {
        if (!LayoutController.hasGPUVersion(layoutType)) {
          console.warn(
            `The '${layoutType}' layout does not support GPU calculation for now, it will run in CPU.`
          )
        } else {
          layoutType = `${layoutType}-gpu` as NormalLayoutConfig['type']
        }
      }

      // 布局结束回调
      layoutConfig.onLayoutEnd = () => {
        subscriber.next(data as LayoutData)
        subscriber.complete()
      }

      // tick 回调
      if (LayoutController.needTick(layoutType)) {
        layoutConfig.tick = () => {
          subscriber.next(data as LayoutData)
        }
      }

      try {
        this.layoutMethod = new Layouts[layoutType](layoutConfig)
      } catch (err) {
        console.warn(
          `The layout method: '${layoutType}' does not exist! Please specify it first.`
        )

        subscriber.error(err)

        return
      }

      if (this.layoutMethod) {
        this.layoutMethod.init(data)
        this.layoutMethod.execute()
      } else {
        subscriber.complete()
      }
    }).pipe(throttleTime(0, animationFrameScheduler))

  /**
   * 获取 worker 对象
   *
   * @private
   */
  private getWorker = () => {
    if (this.worker) {
      return this.worker
    }

    if (typeof Worker === 'undefined') {
      // 如果当前浏览器不支持 web worker，则不使用 web worker
      console.warn('Web worker is not supported in current browser.')

      this.worker = undefined
    } else {
      // this.worker = LayoutsWorker(this.layoutConfig.workerScriptURL)
    }

    return this.worker
  }

  /**
   * 停止 Worker
   *
   * @private
   */
  private stopWorker = () => {
    const { workerData } = this

    if (!this.worker) {
      return
    }

    this.worker.terminate()
    this.worker = undefined

    // 重新开始新的布局之前，先取消之前布局的requestAnimationFrame。
    if (workerData?.requestId) {
      helper.cancelAnimationFrame(workerData.requestId)
      workerData.requestId = undefined
    }

    if (workerData?.requestId2) {
      helper.cancelAnimationFrame(workerData.requestId2)
      workerData.requestId2 = undefined
    }
  }

  /**
   * 运行 web worker
   *
   * @param worker
   * @param data
   * @param layoutConfig
   * @private
   */
  private runWebWorker = (
    worker: Worker,
    data: DraftNormalGraphLayoutData,
    layoutConfig: NormalLayoutConfig
  ): Promise<void> => {
    const { isGPU } = this
    const { nodes, edges } = data
    const normalizedNodes = nodes.map(({ id, x, y, size }) => ({
      id,
      x,
      y,
      size,
    }))
    const normalizedEdges = edges.map(({ source, target }) => ({
      source,
      target,
    }))

    const offScreenCanvas = document.createElement('canvas')
    const gpuWorkerAbility =
      isGPU &&
      typeof window !== 'undefined' &&
      // eslint-disable-next-line @typescript-eslint/dot-notation
      window.navigator &&
      // @ts-ignore
      !navigator.gpu && // WebGPU 还不支持 OffscreenCanvas
      'OffscreenCanvas' in window &&
      'transferControlToOffscreen' in offScreenCanvas

    // NOTE: postMessage的message参数里面不能包含函数，否则postMessage会报错，
    // 例如：'function could not be cloned'。
    // 详情参考：https://developer.mozilla.org/en-US/docs/Web/API/Web_Workers_API/Structured_clone_algorithm
    // 所以这里需要把过滤layoutConfig里的函数字段过滤掉。
    const filteredLayoutCfg = filter(
      layoutConfig,
      (value) => typeof value !== 'function'
    )

    if (!gpuWorkerAbility) {
      worker.postMessage({
        type: LAYOUT_MESSAGE.RUN,
        nodes: normalizedNodes,
        edges: normalizedEdges,
        layoutConfig: filteredLayoutCfg,
      })
    } else {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const offscreen: any =
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (offScreenCanvas as any).transferControlToOffscreen()

      // @ts-ignore
      filteredLayoutCfg.type = `${filteredLayoutCfg.type}-gpu`

      worker.postMessage(
        {
          type: LAYOUT_MESSAGE.GPURUN,
          nodes: normalizedNodes,
          edges: normalizedEdges,
          layoutConfig: filteredLayoutCfg,
          canvas: offscreen,
        },
        [offscreen]
      )
    }

    return new Promise((resolve, reject) => {
      worker.onmessage = (event) => {
        this.handleWorkerMessage(
          resolve,
          reject,
          event,
          {
            nodes: normalizedNodes,
            edges: normalizedEdges,
          },
          layoutConfig
        )
      }
    })
  }

  /**
   * 处理 worker 的消息
   *
   * @param resolve
   * @param reject
   * @param event
   * @param data
   * @param layoutConfig
   * @private
   */
  private handleWorkerMessage = (
    resolve: () => void,
    reject: () => void,
    event: MessageEvent,
    data: DraftNormalGraphLayoutData,
    layoutConfig: NormalLayoutConfig
  ) => {
    // const { graph, workerData } = this
    // const eventData = event.data
    // const { type } = eventData
    // const onTick = () => {}
    // switch (type) {
    //   case LAYOUT_MESSAGE.TICK:
    //     workerData.currentTick = eventData.currentTick
    //     workerData.currentTickData = eventData
    //     if (!workerData.requestId) {
    //       workerData.requestId = helper.requestAnimationFrame(
    //         function requestId() {
    //           updateLayoutPosition(data, eventData)
    //           // graph.refreshPositions()
    //           onTick()
    //           if (eventData.currentTick === eventData.totalTicks) {
    //             // 如果是最后一次tick
    //             resolve()
    //           } else if (workerData.currentTick === eventData.totalTicks) {
    //             // 注意这里workerData.currentTick可能已经不再是前面赋值时候的值了，
    //             // 因为在requestAnimationFrame等待时间里，可能产生新的tick。
    //             // 如果当前tick不是最后一次tick，并且所有的tick消息都已发出来了，那么需要用最后一次tick的数据再刷新一次。
    //             workerData.requestId2 = helper.requestAnimationFrame(
    //               function requestId2() {
    //                 updateLayoutPosition(data, workerData.currentTickData!)
    //                 // graph.refreshPositions()
    //                 workerData.requestId2 = undefined
    //                 onTick()
    //                 resolve()
    //               }
    //             )
    //           }
    //           workerData.requestId = undefined
    //         }
    //       )
    //     }
    //     break
    //   case LAYOUT_MESSAGE.END:
    //     // 如果没有tick消息（非力导布局）
    //     if (workerData.currentTick == null) {
    //       updateLayoutPosition(data, eventData)
    //       resolve()
    //     }
    //     break
    //   case LAYOUT_MESSAGE.GPUEND:
    //     // 如果没有tick消息（非力导布局）
    //     if (workerData.currentTick == null) {
    //       updateGPUWorkerLayoutPosition(data, eventData)
    //       resolve()
    //     }
    //     break
    //   case LAYOUT_MESSAGE.ERROR:
    //     console.warn('Web-Worker layout error!', eventData.message)
    //     reject()
    //     break
    //   default:
    //     reject()
    //     break
    // }
  }

  /* 初始化节点到 center 附近
   *
   * @param center
   * @param nodes
   */
  initPositions = (
    center: [number, number] = [0, 0],
    nodes: DraftNormalGraphLayoutData['nodes'],
    canvasWidth: number,
    canvasHeight: number
  ) => {
    if (!nodes) {
      return false
    }

    const nodeLength = nodes ? nodes.length : 0

    if (!nodeLength) {
      return false
    }

    const width = canvasWidth * 0.85
    const height = canvasHeight * 0.85
    const horizontalNumber = Math.ceil(Math.sqrt(nodeLength) * (width / height))
    const verticalNum = Math.ceil(nodeLength / horizontalNumber)
    let horizontalGap = width / (horizontalNumber - 1)
    let verticalGap = height / (verticalNum - 1)

    if (!isFinite(horizontalGap) || !horizontalGap) {
      horizontalGap = 0
    }

    if (!isFinite(verticalGap) || !horizontalGap) {
      verticalGap = 0
    }

    const beginX = center[0] - width / 2
    const beginY = center[1] - height / 2

    let allHavePos = true

    for (let i = 0; i < nodeLength; i++) {
      const node = nodes[i]

      if (node.x == null || isNaN(+node.x) || node.x === 0) {
        allHavePos = false
        node.x = (i % horizontalNumber) * horizontalGap + beginX
      }

      if (node.y == null || isNaN(+node.y) || node.y === 0) {
        allHavePos = false
        node.y = Math.floor(i / horizontalNumber) * verticalGap + beginY
      }
    }

    return allHavePos
  }

  /**
   * 销毁布局方法
   */
  destroyLayoutMethods = () => {
    if (this.layoutMethod) {
      this.layoutMethod.destroy()
      this.layoutMethod = undefined
    }
  }

  /**
   * 销毁布局
   */
  destroy = () => {
    this.destroyLayoutMethods()

    const { worker } = this

    if (worker) {
      worker.terminate()

      this.worker = undefined
    }

    this.destroyed = true
  }
}
